"use client";

import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CourseModules() {
  const carouselApiRef = useRef<CarouselApi | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const dishes = [{
    callNumber: "MOD. 01",
    name: "Introduction to AI in Business\n",
    description: "•\nWhat AI means for managers\n•\nAI trends shaping industries\n•\nEthical and responsible AI use",
    price: "WK 1",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/workflow_automation_logic.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/workflow_automation_logic.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/workflow_automation_logic.png",
    tags: ["PRACTICAL", "LIVE"],
    stamped: true
  }, {
    callNumber: "MOD. 02",
    name: "AI for Executive Productivity & Time Management\n",
    description: "•\nAI for Communication & Corporate Writing\n•\nAI for scheduling, reporting, and documentation\n•\nBuilding an AI Adoption Strategy for Organizations",
    price: "WK 1",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/learning_environment_focus.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/learning_environment_focus.webp",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/learning_environment_focus.webp",
    tags: ["THEORY"],
    stamped: false
  }, {
    callNumber: "MOD. 03",
    name: "Digital Prototyping",
    description: "•\nTurning data into insights\n•\nPredictive decision support\n•\nAI for Research, Strategy & Business Intelligence\n•\nAI Ethics, Governance & Cybersecurity\n•\nIndustry-Specific AI Applications",
    price: "WK 1",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/ai_prompt_engineering_interface.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/ai_prompt_engineering_interface.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/ai_prompt_engineering_interface.png",
    tags: ["TECH"],
    stamped: false
  }, {
    callNumber: "MOD. 04",
    name: "AI for Marketing, Branding & Customer Engagement\n",
    description: "•\nWriting, summarization, and reporting tools\n•\nAI-assisted presentations\n•\nAI for Customer Experience & Support Systems\n•\nPrompt Engineering for Managers and Corporate Professionals",
    price: "WK 4",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_user_psychology.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_user_psychology.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_user_psychology.png-wvc-sizes",
    tags: ["UX", "CORE"],
    stamped: false
  }, {
    callNumber: "MOD. 05",
    name: "AI for Human Resource Management",
    description: "•\nAI Automation for Corporate Operations\n•\nAI-Powered Project & Team Management\n•\nLeadership in the AI Era\n•\nCapstone Project: Designing AI Solutions for Business Challenges",
    price: "WK 5",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_brand_identity.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_brand_identity.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/module_brand_identity.png-wvc-sizes",
    tags: ["NEW"],
    stamped: false
  }, {
    callNumber: "MOD. 06",
    name: "PRACTICAL SESSIONS",
    description: "Curating your best work into a compelling narrative that captures the attention of top-tier creative directors.",
    price: "WK 3",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_leadership_training_session.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_leadership_training_session.webp",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_leadership_training_session.webp",
    tags: ["CAREER"],
    stamped: false
  }];
  return <section data-section-id="1160" id="course-modules" className="relative overflow-hidden bg-background text-foreground py-28 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(circle at 20% 30%, var(--primary) 0%, transparent 2%), radial-gradient(circle at 70% 60%, var(--secondary) 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, var(--primary) 0%, transparent 2%), radial-gradient(circle at 85% 20%, var(--primary) 0%, transparent 1.5%)",
      backgroundSize: "180px 180px, 240px 240px, 320px 320px, 200px 200px"
    }} aria-hidden="true" />

      <div className="container mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary mb-6">CURRICULUM / 2026 — COURSE MODULES</p>
            <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-tight" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)"
          }}>
              Master the <span className="italic font-serif font-normal">craft,</span> step by step.
            </h2>
            {}
            <div className="mt-8 flex items-center gap-0">
              <span className="block h-px w-16 bg-foreground" />
              <span className="block h-1.5 w-1.5 rounded-md bg-primary" />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-2 flex flex-col justify-between gap-8">
            <p className="font-default text-muted-foreground text-base md:text-lg leading-relaxed max-w-md">
              A comprehensive journey through the modern design landscape. Each module is crafted to build upon the last, ensuring a solid foundation for your creative career.
            </p>
            <div className="flex items-center gap-6">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/70">
                <span className="text-primary mr-2"></span> EXPLORE{" "}
                <span className="text-primary ml-2"></span>
              </p>
              <div className="hidden md:flex items-center gap-2 ml-auto">
                <Button variant="outline" size="icon" onClick={() => carouselApiRef.current?.scrollPrev()} className="h-10 w-10 rounded-md border border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-colors shadow-sm" aria-label="Previous">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => carouselApiRef.current?.scrollNext()} className="h-10 w-10 rounded-md border border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-colors shadow-sm" aria-label="Next">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="mt-16 md:mt-20 pl-6 md:pl-10 lg:pl-16">
        <Carousel opts={{
        align: "start",
        dragFree: true
      }} setApi={api => {
        carouselApiRef.current = api;
      }} className="w-full cursor-grab active:cursor-grabbing">
          <CarouselContent className="-ml-6">
            {dishes.map((dish, i) => <CarouselItem key={i} data-index={i} className="pl-6 basis-[78vw] sm:basis-[60vw] md:basis-[420px] lg:basis-[360px]">
                <article className="group relative h-full bg-card border border-border transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:border-primary/30 rounded-md overflow-hidden">
                  {}
                  {dish.stamped && <div className="pointer-events-none absolute top-6 right-6 z-10 flex h-24 w-24 items-center justify-center rounded-md border-2 border-primary/30 opacity-40" style={{
                transform: "rotate(-7deg)"
              }} aria-hidden="true">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg viewBox="0 0 100 100" className="h-full w-full text-primary">
                          <defs>
                            <path id={`circlePath-${i}`} d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                          </defs>
                          <text className="font-mono fill-current text-[7px] tracking-[0.18em] uppercase">
                            <textPath href={`#circlePath-${i}`}>
                              ACADEMY · 2024 · CERTIFIED · ENROLL NOW ·
                            </textPath>
                          </text>
                        </svg>
                      </div>
                      <div className="flex h-6 w-6 items-center justify-center rounded-md border border-primary/40">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4 text-primary">
                          <circle cx="12" cy="12" r="9" />
                          <circle cx="12" cy="12" r="2" />
                          <line x1="12" y1="3" x2="12" y2="10" />
                          <line x1="12" y1="14" x2="12" y2="21" />
                          <line x1="3" y1="12" x2="10" y2="12" />
                          <line x1="14" y1="12" x2="21" y2="12" />
                        </svg>
                      </div>
                    </div>}

                  {}
                  <div className="relative overflow-hidden aspect-[3/4] bg-muted">
                    <img src={dish.image} data-wvc-srcset={dish.srcset} data-wvc-sizes={dish.sizes} alt={dish.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {}
                  <div className="flex flex-col gap-4 p-7">
                    {}
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                      {dish.callNumber}
                    </p>

                    {}
                    <h3 className="font-default font-bold text-foreground leading-tight relative inline-block w-fit" style={{
                  fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)"
                }}>
                      {dish.name}
                      <span className="absolute -bottom-1 left-0 h-0.5 bg-primary w-0 transition-all duration-300 ease-out group-hover:w-full" aria-hidden="true" />
                    </h3>

                    {}
                    <p className="font-default text-muted-foreground text-[0.95rem] leading-relaxed line-clamp-3">
                      {dish.description}
                    </p>

                    {}
                    <div className="mt-2 flex items-end justify-between gap-3 pt-3 border-t border-border">
                      <div className="flex flex-wrap gap-2">
                        {dish.tags.map((tag, ti) => <Badge key={ti} variant="outline" className="rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                            {tag}
                          </Badge>)}
                      </div>
                      <span className="font-serif font-medium text-primary leading-none" style={{
                    fontSize: "1.5rem"
                  }}>
                        {dish.price}
                      </span>
                    </div>
                  </div>
                </article>
              </CarouselItem>)}

            {}
            <CarouselItem data-index={dishes.length} className="pl-6 basis-6 md:basis-10 lg:basis-16" aria-hidden="true">
              <div className="h-1" />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>
    </section>;
}